import { Button, FormControl, FormErrorMessage, Stack, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { serverUrl } from '../config';
import { TextField } from './TextField';
import { ConfirmPasswordSchema, PasswordSchema } from './Validation';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';

interface UpdatePassword {
  newPassword: string;
  oldPassword: string;
}

const postUpdatePassword = (token: string, updatePassword: UpdatePassword) =>
  fetch(`${serverUrl}/api/actions/update-password`, {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Token ${token}`,
    },
    body: JSON.stringify(updatePassword),
  }).then((res) => res.json());

const initialFormValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

type PasswordResetValues = typeof initialFormValues;

const PasswordResetSchema = Yup.object().shape({
  oldPassword: Yup.string().max(100).required('You must enter your old password'),
  newPassword: PasswordSchema,
  confirmPassword: ConfirmPasswordSchema('newPassword'),
});

const submitPasswordReset =
  (token: string) =>
  async (
    values: PasswordResetValues,
    setSubmissionError: (error: string) => void,
    onSuccess: () => void
  ) => {
    try {
      const res = await postUpdatePassword(token, {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      if (res.status === 'error' || res.errors) {
        setSubmissionError(res.message);
      } else {
        onSuccess();
      }
    } catch (error) {
      setSubmissionError('Something went wrong. Please try again in a minute.');
    }
  };

const useToastSuccess = () => {
  const toast = useToast();

  return useCallback(() => {
    toast({
      title: 'Success',
      description: 'Your password has been changed',
      status: 'success',
      position: 'top',
    });
  }, [toast]);
};

const useResetPassword = () => {
  const [submissionError, setSubmissionError] = useState<string | undefined>();
  const onSuccess = useToastSuccess();

  const authedPasswordReset = useAuthenticateApiCall(submitPasswordReset);

  const onSubmit = useCallback(
    async (values: PasswordResetValues) => {
      setSubmissionError(undefined);
      await authedPasswordReset(values, setSubmissionError, onSuccess);
    },
    [onSuccess, setSubmissionError, authedPasswordReset]
  );

  return { onSubmit, submissionError };
};

export const PasswordResetForm = () => {
  const { onSubmit, submissionError } = useResetPassword();

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={PasswordResetSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <Form>
          <Stack spacing={4}>
            <TextField
              name="oldPassword"
              label="Old Password"
              placeholder="Old password"
              type="password"
              error={errors.oldPassword}
              touched={touched.oldPassword}
            />
            <TextField
              name="newPassword"
              label="New Password"
              placeholder="New password"
              type="password"
              error={errors.newPassword}
              touched={touched.newPassword}
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Confirm password"
              type="password"
              error={errors.confirmPassword}
              touched={touched.confirmPassword}
            />

            <FormControl isInvalid={submissionError !== undefined}>
              <FormErrorMessage>{submissionError}</FormErrorMessage>
            </FormControl>
          </Stack>
          <Button
            mt={4}
            isLoading={isSubmitting}
            isDisabled={!isValid}
            loadingText="Changing password"
            type="submit"
          >
            Change Password
          </Button>
        </Form>
      )}
    </Formik>
  );
};
